import {
  MemberCreate,
  MemberGrantResponse,
  MemberResponse,
  createMember,
  deleteMember,
  fetchMembers,
  updateMemberGrant,
} from "@/api/member";
import { useAuth } from "@/app/providers";
import { useAuthStore } from "@/app/store/useAuthStore";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { toast } from "@/shared/hooks/useToast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

// ── AddMemberModal ─────────────────────────────────────────────────────────────

interface AddMemberModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: MemberCreate) => void;
  isPending: boolean;
}

function isValidEmail(val: string) {
  return /\S+@\S+\.\S+/.test(val);
}

function AddMemberModal({ open, onClose, onAdd, isPending }: AddMemberModalProps) {
  const [email, setEmail] = useState("");
  const [grant, setGrant] = useState<"member" | "admin">("member");

  useEffect(() => {
    if (!open) {
      setEmail("");
      setGrant("member");
    }
  }, [open]);

  function handleClose() {
    onClose();
  }

  function handleAdd() {
    if (!isValidEmail(email.trim()) || isPending) return;
    onAdd({ email: email.trim(), grant });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>사용자 추가</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label htmlFor="add-member-email" className="text-sm text-muted-foreground block mb-1">이메일</label>
            <input
              id="add-member-email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (isValidEmail(email.trim())) handleAdd();
                }
              }}
              className="w-full border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div>
            <label htmlFor="add-member-grant" className="text-sm text-muted-foreground block mb-1">권한</label>
            <select
              id="add-member-grant"
              value={grant}
              onChange={(e) => setGrant(e.target.value as "member" | "admin")}
              className="w-full border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="member">멤버</option>
              <option value="admin">관리자</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>취소</Button>
          <Button disabled={!isValidEmail(email.trim()) || isPending} onClick={handleAdd}>확인</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── ManageMembersPage ─────────────────────────────────────────────────────────

export function ManageMembersPage() {
  const qc = useQueryClient();
  const { logout } = useAuth();
  const { grant, memberId: currentMemberId, setGrant } = useAuthStore();
  const isAdmin = grant === "admin";

  const [addOpen, setAddOpen] = useState(false);
  const [deleteSelfConfirmOpen, setDeleteSelfConfirmOpen] = useState(false);
  const [demoteSelfConfirmOpen, setDemoteSelfConfirmOpen] = useState(false);
  const [grantMap, setGrantMap] = useState<Map<number, "member" | "admin">>(new Map());

  const { data: members = [], isLoading, isError } = useQuery<MemberResponse[]>({
    queryKey: ["members"],
    queryFn: fetchMembers,
    enabled: isAdmin,
  });

  useEffect(() => {
    setGrantMap(new Map(members.map((m) => [m.id, m.grant ?? "member"])));
  }, [members]);

  const addMut = useMutation({
    mutationFn: (data: MemberCreate) => createMember(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["members"] });
      setAddOpen(false);
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "멤버 추가 실패", description: error.message || "멤버 추가 중 오류가 발생했습니다." });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (memberId: number) => {
      await deleteMember(memberId);
      return memberId;
    },
    onSuccess: (deletedId) => {
      qc.invalidateQueries({ queryKey: ["members"] });
      if (deletedId === currentMemberId) {
        logout();
      }
    },
    onError: (error: Error) => {
      const message = error.message || "";
      const isRotation = message.includes("schedule rotation");
      toast({
        variant: "destructive",
        title: "삭제 실패",
        description: isRotation
          ? "스케줄 로테이션에 포함된 멤버입니다. 해당 멤버가 포함된 스케줄을 먼저 정리해주세요."
          : "삭제하지 못했습니다.",
      });
    },
  });

  const grantMut = useMutation({
    mutationFn: ({ memberId, grant }: { memberId: number; grant: "member" | "admin" }) =>
      updateMemberGrant(memberId, grant),
    onMutate: ({ memberId }) => {
      const previousGrant = grantMap.get(memberId) ?? "member";
      return { previousGrant, memberId };
    },
    onSuccess: (res: MemberGrantResponse) => {
      setGrantMap((prev) => new Map(prev).set(res.member_id, res.grant));
      if (res.member_id === currentMemberId) {
        setGrant(res.grant);
      }
    },
    onError: (_error, _variables, context) => {
      if (context) {
        setGrantMap((prev) => new Map(prev).set(context.memberId, context.previousGrant));
      }
      const msg = _error instanceof Error ? _error.message : "";
      toast({ variant: "destructive", title: "권한 변경 실패", description: msg || "멤버 권한 변경 중 오류가 발생했습니다." });
    },
  });

  const sortedMembers = currentMemberId !== null
    ? [
        ...members.filter((m) => m.id === currentMemberId),
        ...members.filter((m) => m.id !== currentMemberId),
      ]
    : members;

  function handleDeleteClick(memberId: number) {
    if (memberId === currentMemberId) {
      setDeleteSelfConfirmOpen(true);
      return;
    }
    deleteMut.mutate(memberId);
  }

  // 메뉴는 관리자에게만 보이지만 URL 직접 진입은 대시보드로 돌려보낸다
  if (!isAdmin) {
    return <Navigate to="/overview" replace />;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold">사용자 관리</h1>
        <Button onClick={() => setAddOpen(true)}>사용자 추가</Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">불러오는 중…</p>
      ) : isError ? (
        <p className="text-sm text-destructive">멤버 목록을 불러오는 중 오류가 발생했습니다.</p>
      ) : members.length === 0 ? (
        <div className="border border-dashed rounded-lg p-10 text-center text-sm text-muted-foreground">
          멤버가 없습니다.
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left px-4 py-3 font-semibold">이름</th>
                <th className="text-left px-4 py-3 font-semibold">이메일</th>
                <th className="text-left px-4 py-3 font-semibold">전화번호</th>
                <th className="text-left px-4 py-3 font-semibold">권한</th>
                <th scope="col" className="px-4 py-3 pr-8 w-16" aria-label="작업" />
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map((member) => {
                const currentGrant = grantMap.get(member.id) ?? member.grant ?? "member";
                const isSelf = member.id === currentMemberId;
                return (
                  <tr key={member.id} className={`border-b last:border-0 ${isSelf ? "bg-primary/5" : ""}`}>
                    <td className="px-4 py-3">{member.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{member.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{member.phone}</td>
                    <td className="px-4 py-3">
                      <select
                        value={currentGrant}
                        disabled={grantMut.isPending}
                        aria-label={`${member.name} 권한 변경`}
                        onChange={(e) => {
                          const newGrant = e.target.value as "member" | "admin";
                          if (member.id === currentMemberId && newGrant === "member") {
                            setDemoteSelfConfirmOpen(true);
                            return;
                          }
                          grantMut.mutate({ memberId: member.id, grant: newGrant });
                        }}
                        className="border rounded px-2 py-1 text-sm bg-background disabled:opacity-50"
                      >
                        <option value="member">멤버</option>
                        <option value="admin">관리자</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 pr-8 text-right">
                      <button
                        type="button"
                        aria-label={`${member.name} 삭제`}
                        className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={deleteMut.isPending}
                        onClick={() => handleDeleteClick(member.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AddMemberModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={(data) => addMut.mutate(data)}
        isPending={addMut.isPending}
      />

      <Dialog open={deleteSelfConfirmOpen} onOpenChange={setDeleteSelfConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>본인 계정 삭제</DialogTitle>
          </DialogHeader>
          <p className="text-sm">본인 계정을 삭제하시겠습니까?<br />삭제 후 탈퇴 및 로그아웃됩니다.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSelfConfirmOpen(false)}>취소</Button>
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteSelfConfirmOpen(false);
                if (currentMemberId !== null) {
                  deleteMut.mutate(currentMemberId);
                }
              }}
            >
              확인
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={demoteSelfConfirmOpen} onOpenChange={setDemoteSelfConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>본인 권한 변경</DialogTitle>
          </DialogHeader>
          <p className="text-sm">본인 권한을 멤버로 변경하시겠습니까?<br />변경 후 본 페이지에 접근할 수 없습니다.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoteSelfConfirmOpen(false)}>취소</Button>
            <Button
              variant="destructive"
              onClick={() => {
                setDemoteSelfConfirmOpen(false);
                if (currentMemberId !== null) {
                  grantMut.mutate({ memberId: currentMemberId, grant: "member" });
                }
              }}
            >
              확인
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
